import React, { useState, useContext, useEffect, useMemo, useRef } from "react";
import ReactDOM from "react-dom";
import { Tooltip } from "../Tooltip";
import { useHistory } from "react-router-dom";
import styles from "./NewQuery.module.css";
import { isCpmInput, validateCpm } from "./cpm";
import SearchIcon from "../../assets/search.svg";
import SimpleBack from "../../assets/simpleBackIcon.svg";
import DeleteIcon from "../../resources/icon-delete.svg";
import EditIcon from "../../assets/edit.svg";
import { AuthContext } from "../../contexts/auth-context";
import Typeahead from "../Typeahead";
import { useBreadcrumb } from "../Breadcrumb/BreadcrumbContext";
import { useSidebar } from "../NewSideBar/SidebarContext";
import { useAsyncFn } from "../../hooks/use-async-fn";
import PlusIcon from "../../assets/plusIcon.svg";
import DoneIcon from "../../assets/done.svg";
import InfoPopup from "../InfoPopup";
import PageNotFound from "../PageNotFound";
import InforIcon from "../../assets/InfoCircleIcon";
import { ReloadIcon } from "../../assets/ReloadIcon";
import LayoutContainer from "../LayoutContainer";
import { capitalize, getQueryWarningMessage } from "../../utils";
import LimitsUsageBanner from "../LimitsUsageBanner";
import { useToast } from "../../contexts/toast-context";
import {
  useLimitsUsage,
  QueryTypeUsage,
  tableUsageMatchesSelection,
} from "../../hooks/use-limits-usage";
import { OI, Loader16 } from "../../onam/mark";
import { FEATURES } from "../../onam/features";

interface QueryCondition {
  attribute: string;
  operator: string;
  value: string;
  logicalOperator: string;
  objective: string;
  enrichmentAttributes: any;
}

const logicalOperators = ["AND", "OR"];

const arithmeticOperators = [
  { value: "lte", label: "Less than or equal to" },
  { value: "lt", label: "Less than" },
  { value: "gte", label: "Greater than or equal to" },
  { value: "gt", label: "Greater than" },
  { value: "eq", label: "Equals" },
  { value: "neq", label: "Not equals" },
  { value: "is", label: "is" },
  { value: "not", label: "not" },
];
const NewQuery: React.FC = () => {
  const {
    getAttributes,
    putquery,
    user,
    getCampaignNames,
    putCampaignName,
    getFednDetails,
    appMetadata,
    applicationId,
  } = useContext(AuthContext);

  const { setBreadcrumbItems } = useBreadcrumb();
  const { mainMenuItems, setActiveSubItem, setIsSideBarMenuDisabled } =
    useSidebar();
  const matchId = localStorage.getItem("matchid");

  const bucketIds = JSON.parse(localStorage.getItem("bucketids") || "[]");

  // const namesOrg = localStorage.getItem("namesOrg");

  var sessionFederationId = localStorage.getItem("federationkey");

  var sessionOrganizationId = localStorage.getItem("organizationkey");
  const getFednDetailsFn = useAsyncFn({
    promiseFn: getFednDetails,
    federationID: sessionFederationId,
  });
  const allowedQueryObjectives = JSON.parse(
    localStorage.getItem("allowedQueryObjectives"),
  );

  const [enrichmentAttributes, setEnrichmentAttributes] = useState(
    getFednDetailsFn?.data?.query_objective_parameters?.enrichment
      ?.attributes || [],
  );

  const datasetAttributeNames = useMemo(() => {
    const selectedBucketObjects = JSON.parse(
      localStorage.getItem("selectedBucketObjects") || "[]",
    );
    return new Set<string>(
      selectedBucketObjects.flatMap((b: any) =>
        Array.isArray(b.attributes) ? b.attributes.map((a: any) => a.name) : [],
      ),
    );
  }, []);

  // Org name paired with table name, not table name alone - the same
  // physical_table_name can be contributed by more than one org in a
  // federation (see use-limits-usage's TableUsage comment), so matching on
  // table name only would also pull in an unrelated org's usage row.
  //
  // selectedBucketObjects in localStorage is written by the
  // IdentitiesAttributes page before navigating here, but on browser
  // back/forward this component can stay mounted while that value changes
  // underneath it - so this must not be pinned to a mount-only memo.
  // Re-reading the raw string on every render (cheap) and keying the memo
  // on it keeps selectedTables in sync with localStorage while still
  // preserving a stable array reference (and downstream memoization, e.g.
  // relevantTableUsage below) across unrelated re-renders.
  const selectedBucketObjectsRaw =
    localStorage.getItem("selectedBucketObjects") || "[]";
  const selectedTables = useMemo(() => {
    const selectedBucketObjects = JSON.parse(selectedBucketObjectsRaw);
    return selectedBucketObjects
      .filter((b: any) => b.external_table_name)
      .map((b: any) => ({
        organizationName: b.organization_name,
        tableName: b.external_table_name,
      }));
  }, [selectedBucketObjectsRaw]);

  const { fetchUsage: fetchLimitsUsageData, loading: limitsUsageLoading } =
    useLimitsUsage();
  const [objectiveUsage, setObjectiveUsage] = useState<
    QueryTypeUsage | undefined
  >(undefined);

  const filteredEnrichmentAttributes = useMemo(
    () =>
      datasetAttributeNames.size > 0
        ? enrichmentAttributes.filter((attr: string) => datasetAttributeNames.has(attr))
        : enrichmentAttributes,
    [enrichmentAttributes, datasetAttributeNames],
  );

  var orgId =
    sessionOrganizationId ||
    (Object.values(appMetadata || {}) as any[])[0]?.organization;
  const [conditions, setConditions] = useState<QueryCondition[]>([]);
  const [formFields, setFormFields] = useState<QueryCondition>({
    attribute: "",
    operator: "",
    value: "",
    logicalOperator: "",
    objective: "",
    enrichmentAttributes: [],
  });
  const [inputValue, setInputValue] = useState({ id: "", name: "" });
  const [description, setDescription] = useState("");
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [showPlusIcon, setShowPlusIcon] = useState(false);
  const [, setShowTickIcon] = useState(false);
  const [campaignNames, setCampaignNames] = useState([]);
  const [isCampaignAdded, setIsCampaignAdded] = useState(false);
  const [loading, showLoader] = useState(false);
  const [showErrorMessage, setShowErrorMessage] = useState("");
  const [displayErrorMessage, setDisplayErrorMessage] = useState("");
  const [showInfoPopup, setShowInfoPopup] = useState(false);
  const { showToast } = useToast();
  const [selectedAttributes, setSelectedAttributes] = useState([]);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [campaignNameError, setCampaignNameError] = useState("");
  const [cpm, setCpm] = useState("");
  const [cpmError, setCpmError] = useState("");
  const [descriptionError, setDescriptionError] = useState("");
  const [validationError, setValidationError] = useState("");
  const [duplicatePending, setDuplicatePending] = useState<QueryCondition | null>(null);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const nameFieldRef = useRef<HTMLDivElement>(null);
  const dropdownPortalRef = useRef<HTMLDivElement>(null);
  const [dropdownRect, setDropdownRect] = useState<{ top: number; left: number; width: number } | null>(null);

  useEffect(() => {
    if (isDropdownOpen && nameFieldRef.current) {
      const r = nameFieldRef.current.getBoundingClientRect();
      setDropdownRect({ top: r.bottom, left: r.left, width: r.width });
    } else {
      setDropdownRect(null);
    }
  }, [isDropdownOpen, inputValue?.name]);

  useEffect(() => {
    if (!isDropdownOpen) return;
    const close = () => setIsDropdownOpen(false);
    const handleMouseDown = (e: MouseEvent) => {
      if (
        nameFieldRef.current && !nameFieldRef.current.contains(e.target as Node) &&
        dropdownPortalRef.current && !dropdownPortalRef.current.contains(e.target as Node)
      ) {
        close();
      }
    };
    const handleScroll = (e: Event) => {
      if (dropdownPortalRef.current && dropdownPortalRef.current.contains(e.target as Node)) return;
      close();
    };
    document.addEventListener("mousedown", handleMouseDown);
    document.addEventListener("scroll", handleScroll, true);
    return () => {
      document.removeEventListener("mousedown", handleMouseDown);
      document.removeEventListener("scroll", handleScroll, true);
    };
  }, [isDropdownOpen]);

  const toggleAttribute = (attr) => {
    let updatedAttrs = [];
    if (selectedAttributes.includes(attr)) {
      updatedAttrs = selectedAttributes.filter((a) => a !== attr);
    } else {
      updatedAttrs = [...selectedAttributes, attr];
    }

    setSelectedAttributes(updatedAttrs);

    setFormFields({
      ...formFields,
      enrichmentAttributes: updatedAttrs,
    });
  };

  const handleDescriptionChange = (e) => {
    const val = e.target.value;
    if (val.length > 100) {
      setDescription(val.slice(0, 100));
      setDescriptionError("Description cannot exceed 100 characters.");
    } else {
      setDescription(val);
      setDescriptionError("");
    }
  };
  const handleCpmChange = (e) => {
    const val = e.target.value;
    if (!isCpmInput(val)) return;
    setCpm(val);
    // Don't flag an untouched field; once an error is showing, track it live.
    if (cpmError) setCpmError(validateCpm(val).error);
  };
  const handleInputChange = (e) => {
    const val = e.target.value;
    setIsCampaignAdded(false);
    setShowTickIcon(false);
    setShowPlusIcon(val.length > 0);
    if (val.length > 40) {
      setInputValue({ id: "", name: val.slice(0, 40) });
      setCampaignNameError("Cohort name cannot exceed 40 characters.");
    } else {
      setInputValue({ id: "", name: val });
      setCampaignNameError("");
    }
    setIsDropdownOpen(val.length > 0);
  };

  const handleSelect = (campaign) => {
    setShowTickIcon(true);
    setShowPlusIcon(false);
    setInputValue({ id: campaign.id, name: campaign.name });
    setIsDropdownOpen(false);
  };

  const history = useHistory();
  const getAttributesFn = useAsyncFn({
    deferFn: getAttributes,
  });
  const putqueryFn = useAsyncFn({
    deferFn: putquery,
  });
  const getCampaignNamesFn = useAsyncFn({
    deferFn: getCampaignNames,
  });
  const putCampaignNameFn = useAsyncFn({
    deferFn: putCampaignName,
  });
  var sortedAttributes = getAttributesFn.data && getAttributesFn.data;
  useEffect(() => {
    if (matchId && bucketIds) {
      getAttributesFn.run({
        federationId: sessionFederationId,
        organizationId: orgId,
        identifier: "",
        canonical_identity_type: matchId,
        buckets: bucketIds,
      });
    }
    const fetchCampaignData = async () => {
      if (!orgId) {
        console.error("Organization ID is missing — skipping campaign fetch.");
        return;
      }
      try {
        const result = await getCampaignNamesFn.run({ orgId });
        setCampaignNames(
          result?.Items.map((item) => ({ id: item.id, name: item.name })),
        );
      } catch (error) {
        console.error("Error fetching campaign data:", error);
      }
    };

    fetchCampaignData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/exhaustive-deps
    getFednDetailsFn.run({
      promiseFn: getFednDetails,
      federationID: sessionFederationId,
    });
  }, [sessionFederationId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    setEnrichmentAttributes(
      getFednDetailsFn?.data?.query_objective_parameters?.enrichment
        ?.attributes || [],
    );
  }, [getFednDetailsFn?.data]);

  const { given_name, family_name } = user || {};
  var user_name = given_name + " " + family_name;

  const invokeQuery = async (
    filters,
    campaign_id = null,
    resolvedOrgId?: string,
  ) => {
    const effectiveOrgId =
      resolvedOrgId || localStorage.getItem("organizationkey");
    try {
      const body = {
        user_name: user_name,
        federation_id: sessionFederationId,
        identifier: "",
        canonical_identity_type: matchId,
        inquirer_organization_id: effectiveOrgId,
        inquirer_bucket_id: "",
        contributor_bucket: bucketIds,
        attributes_selected: formFields.enrichmentAttributes,
        filters: filters,
        campaign_name: inputValue?.name,
        campaign_id: campaign_id || inputValue?.id,
        description,
        cpm: validateCpm(cpm).value,
        override_empty_predicate: displayErrorMessage !== "",
        objective: formFields?.objective,
      };

      const result = await putqueryFn.run({
        body,
        fedId: sessionFederationId,
        orgId: effectiveOrgId,
        intermediate: true,
        applicationId,
      });

      showLoader(false);
      const warningMessage = getQueryWarningMessage(result);
      if (warningMessage) showToast(warningMessage, "warning");
      history.push("/campaign-reports");
      setDisplayErrorMessage("");
    } catch (error) {
      console.error("Error invoking query:", error);
      showLoader(false);

      const errorMessage = error?.message || "An unexpected error occurred.";

      // 5xx (or a missing/unknown status, e.g. a network failure) is a hard
      // server/infra failure with nothing for the user to override — always
      // a stop, regardless of message text. Only a 4xx business-rule
      // rejection (limit reached, predicate-validation warning, etc.) is
      // ever offered as a "do you still want to proceed?" retry.
      const status = error?.status;
      if (!status || status >= 500) {
        setShowErrorMessage(errorMessage);
      } else {
        setDisplayErrorMessage(errorMessage);
      }
    }
  };

  const getAttributeDisplayName = (attributeKey: string) => {
    const item = sortedAttributes?.[attributeKey];
    return item?.label?.trim() || item?.name || attributeKey;
  };

  const validateConditions = () => {
    const attributeGroups: { [key: string]: QueryCondition[] } = {};
    conditions.forEach((condition) => {
      if (!attributeGroups[condition.attribute]) {
        attributeGroups[condition.attribute] = [];
      }
      attributeGroups[condition.attribute].push(condition);
    });

    for (const attribute in attributeGroups) {
      const conditionsForAttribute = attributeGroups[attribute];
      const logicalOperators = new Set(
        conditionsForAttribute.map((c) => c.logicalOperator).filter(Boolean),
      );
      const attributeName = getAttributeDisplayName(attribute);

      if (conditionsForAttribute.length > 1 && logicalOperators.size === 0) {
        setValidationError(
          `AND or OR operator is required when multiple values exist for the attribute: ${attributeName}`,
        );
        return false;
      }

      if (logicalOperators.size > 1) {
        setValidationError(
          `Only one Logical Operator can be applied for the attribute: ${attributeName}`,
        );
        return false;
      }
    }

    return true;
  };

  const commitCondition = (condition: QueryCondition) => {
    setConditions((prev) => {
      if (editingIndex === null) return [...prev, condition];
      const updated = [...prev];
      updated[editingIndex] = condition;
      return updated;
    });
    setFormFields({
      attribute: "",
      operator: "",
      value: "",
      logicalOperator: "",
      objective: condition.objective,
      enrichmentAttributes: condition.enrichmentAttributes,
    });
    setDuplicatePending(null);
    setEditingIndex(null);
  };

  const handleAddCondition = () => {
    if (!formFields.attribute || !formFields.operator || !formFields.value)
      return;
    const isDuplicate = conditions.some(
      (c, i) =>
        i !== editingIndex &&
        c.attribute === formFields.attribute &&
        c.operator === formFields.operator &&
        c.value === formFields.value,
    );
    if (isDuplicate) {
      setDuplicatePending({ ...formFields });
    } else {
      commitCondition(formFields);
    }
  };

  const handleEditCondition = (index: number) => {
    setFormFields({ ...conditions[index] });
    setEditingIndex(index);
  };

  const handleCancelEdit = () => {
    setFormFields((prev) => ({
      attribute: "",
      operator: "",
      value: "",
      logicalOperator: "",
      objective: prev.objective,
      enrichmentAttributes: prev.enrichmentAttributes,
    }));
    setEditingIndex(null);
  };

  const handleDeleteCondition = (index: number) => {
    setConditions(conditions.filter((_, i) => i !== index));
    if (editingIndex === index) {
      handleCancelEdit();
    } else if (editingIndex !== null && index < editingIndex) {
      setEditingIndex(editingIndex - 1);
    }
  };

  // One element, not a component defined inside this one: a component made anew on every render was remounted each
  // time the page re-rendered during a search (each request's state change), restarting its fade, so it flickered.
  const loadingSpinner = (
    <div className={styles.spinnerOverlay}>
      <div className={styles.spinner}></div>
    </div>
  );

  const handleSearch = async () => {
    if (!validateConditions()) return;
    const cpmResult = validateCpm(cpm);
    if (cpmResult.value === null) {
      setCpmError(cpmResult.error);
      return;
    }
    const currentOrgId = orgId;
    if (!currentOrgId) {
      setShowErrorMessage("Organization ID is missing. Please try again.");
      return;
    }
    showLoader(true);
    // if (!matchId || !bucketIds) {
    //   alert("Please choose an identifier & organization(s) to query from!");
    //   history.push("campaign-planning");
    // }
    const filtersMap: {
      [key: string]: { attribute: string; conditions: any[] };
    } = {};

    conditions.forEach(({ attribute, operator, value, logicalOperator }) => {
      if (!filtersMap[attribute]) {
        // `attribute` here is the option key (an array index once attributes
        // come back as a list), not the real column name — resolve it back
        // to `name` for the payload the backend actually understands.
        const attributeName = sortedAttributes?.[attribute]?.name || attribute;
        filtersMap[attribute] = { attribute: attributeName, conditions: [] };
      }

      if (logicalOperator) {
        filtersMap[attribute].conditions.push({
          operator: "logical",
          value: logicalOperator,
        });
      }

      filtersMap[attribute].conditions.push({
        operator,
        value,
      });
    });

    const filters = Object.values(filtersMap);
    if (!campaignNames.some((campaign) => campaign.name === inputValue?.name)) {
      try {
        const response = await putCampaignNameFn.run({
          body: {
            name: inputValue?.name,
            description: description.trim(),
            federation_id: sessionFederationId,
            organization_id: currentOrgId,
          },
          organizationId: currentOrgId,
        });

        if (response?.id) {
          setInputValue((prev) => ({
            ...prev,
            id: response.id,
          }));
          invokeQuery(filters, response.id, currentOrgId);
        }
      } catch (error: any) {
        showLoader(false);
        const msg =
          error?.message || "Failed to create campaign. Please try again.";
        setCampaignNameError(msg.charAt(0).toUpperCase() + msg.slice(1));
      }
    } else {
      invokeQuery(filters, null, currentOrgId);
    }
  };

  useEffect(() => {
    const sideBarMenuToHighlight = mainMenuItems
      .flatMap((item) => item.subMenu)
      .find((subItem) =>
        subItem.urls.some((url) => url.includes("/campaign-planning")),
      )?.label;
    setActiveSubItem(sideBarMenuToHighlight);
    setIsSideBarMenuDisabled({
      fed: false,
      cp: false,
      cr: false,
      pr: false,
      ah: false,
      ca: false,
      dm: false,
    });
    const fedSpecificMenuLabels = JSON.parse(
      localStorage.getItem("fedSpecificMenuLabels") || "{}",
    );
    setBreadcrumbItems([
      { label: "Discover", path: "/entry-hub?console=Discover" },
      { label: "Curation", path: "/federation-selection" },
      { label: "Federation Selection", path: "/federation-selection" },
      {
        label: fedSpecificMenuLabels?.campaign_planning || "Audience Curation",
        path: "/campaign-planning",
      },
      { label: "New Query", path: "/new-query" },
    ]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const storedPermissions = JSON.parse(
    localStorage.getItem("userPermissions") || "{}",
  );

  // Only surface dataset context (group headers, badges, breadcrumb) once
  // there's actually more than one dataset to disambiguate between.
  const distinctDatasetCount = useMemo(() => {
    if (!sortedAttributes) return 0;
    return new Set(
      Object.values(sortedAttributes)
        .map((item: any) => item.dataset_name)
        .filter(Boolean),
    ).size;
  }, [sortedAttributes]);

  const attributeOptions = sortedAttributes
    ? Object.keys(sortedAttributes).map((attr) => {
        const item = sortedAttributes[attr];
        const displayText = item.label?.trim() ? item.label : item.name;
        const datasetContext =
          distinctDatasetCount > 1 ? item.dataset_name || undefined : undefined;
        return {
          value: attr,
          label: displayText,
          sublabel: datasetContext,
          group: datasetContext,
        };
      })
    : null;

  const objectiveOptions =
    allowedQueryObjectives?.map((obj) => ({
      label: capitalize(obj),
      value: obj,
    })) || [];

  useEffect(() => {
    if (allowedQueryObjectives?.length === 1) {
      setFormFields((prev) => ({
        ...prev,
        objective: allowedQueryObjectives[0],
      }));
    }
  }, [allowedQueryObjectives]);

  const handleRefreshAttributes = async () => {
    setIsRefreshing(true);

    try {
      await getFednDetailsFn.run({
        promiseFn: getFednDetails,
        federationID: sessionFederationId,
      });
    } catch (err) {
      console.error("Refresh failed", err);
    } finally {
      setIsRefreshing(false);
    }
  };

  const filteredCampaigns = isDropdownOpen
    ? campaignNames.filter((c) =>
        c.name.toLowerCase().includes(inputValue?.name?.toLowerCase() ?? ""),
      )
    : [];

  const isEnrichmentObjective =
    formFields.objective === "enrichment" ||
    formFields.objective === "audienceMatching_enrichment";

  useEffect(() => {
    if (!formFields.objective || !sessionFederationId || !orgId) {
      setObjectiveUsage(undefined);
      return;
    }
    let cancelled = false;
    fetchLimitsUsageData([formFields.objective], {
      organizationId: orgId,
      federationId: sessionFederationId,
    }).then((results) => {
      if (!cancelled) setObjectiveUsage(results[0]?.usage);
    });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formFields.objective, sessionFederationId, orgId]);

  const isEnrichmentAttributesRequired =
    isEnrichmentObjective &&
    filteredEnrichmentAttributes.length > 0 &&
    (!formFields.enrichmentAttributes ||
      formFields.enrichmentAttributes.length === 0);

  const hasNoEnrichmentAttributesAvailable =
    isEnrichmentObjective &&
    !isRefreshing &&
    filteredEnrichmentAttributes.length === 0;

  const isEnrichmentSearchDisabled =
    isEnrichmentAttributesRequired || hasNoEnrichmentAttributesAvailable;

  // FR-20 Task 1 AC8: proactively block submission instead of only relying
  // on the backend's 4xx rejection (enforceTableQueryLimits) after a wasted
  // round trip. Scoped to the datasets this query is actually built against,
  // same as LimitsUsageBanner's selectedTables filter below.
  const relevantTableUsage = useMemo(
    () =>
      objectiveUsage?.table_usage?.filter((t) =>
        tableUsageMatchesSelection(t, selectedTables),
      ) ?? [],
    [objectiveUsage, selectedTables],
  );

  const blockingTableUsage = relevantTableUsage.find(
    (t) => !t.enabled_flag || t.query_count >= t.query_limit,
  );

  const tableLimitBlockedMessage = blockingTableUsage
    ? !blockingTableUsage.enabled_flag
      ? "This table is disabled. Contact administrator."
      : "Query limit reached. Contact administrator."
    : null;

  // ONAM skin (FEATURES.curationSkin): the four steps keep their places from the start, each marked to do, now or
  // done on one connected rail; the Search footer says what is still needed.
  const skin = FEATURES.curationSkin;
  const attrPicked = !!formFields.attribute;
  const stepState = (done: boolean, now: boolean) => (done ? "done" : now ? "now" : "todo");
  const opsForAttribute = ["varchar", "string", "boolean"].includes(
    sortedAttributes?.[formFields.attribute]?.type?.toLowerCase(),
  )
    ? arithmeticOperators.filter((op) => op.value === "eq" || op.value === "neq")
    : arithmeticOperators;
  const nextHint = !attrPicked
    ? "Start with an attribute"
    : !formFields.operator
      ? "Choose an operator"
      : !formFields.value
        ? "Choose a value"
        : editingIndex !== null
          ? "Ready to update"
          : "Ready to add";
  const stillNeeded = [
    !formFields.objective && "an objective",
    isEnrichmentSearchDisabled && "enrichment attributes",
    Object.keys(sortedAttributes || {}).length > 0 && conditions.length === 0 && "at least one condition",
    !inputValue?.name && "a cohort name",
    validateCpm(cpm).value === null && "a CPM",
  ].filter(Boolean) as string[];
  const listed = (xs: string[]) =>
    xs.length < 2 ? xs.join("") : `${xs.slice(0, -1).join(", ")} and ${xs[xs.length - 1]}`;
  const emptySlot = <div className="nq-slot" aria-hidden="true" />;

  return storedPermissions?.query !== "full" ? (
    <PageNotFound />
  ) : (
    <LayoutContainer>
      <div className={styles.actionsBar}>
        <button
          className={styles.backButton}
          onClick={() => history.push("/campaign-planning")}
        >
          <img src={SimpleBack} alt="back" />
          Back
        </button>
        <InforIcon
          className={styles.infoIcon}
          onClick={() => setShowInfoPopup(true)}
        />
      </div>
      {showInfoPopup && (
        <InfoPopup
          displayText={
            "Select the attibutes, operators and their values and click Add button to create a query parameter. By Default we add AND operator between each parameter. Users can select multple values for same attribute by using the Logical operator AND , OR from the drop down."
          }
          isOpen={showInfoPopup}
          onClose={() => setShowInfoPopup(false)}
          closeButtonLabel="Got it"
          headerText="Information"
          freeText
        />
      )}
      <div className={styles.pageContainer} aria-busy={(skin && loading) || undefined}>
        {displayErrorMessage && (
          <InfoPopup
            headerText="Warning"
            displayText={`${displayErrorMessage}\n\nDo you still want to proceed?`}
            freeText
            warning
            isOpen={displayErrorMessage !== ""}
            onClose={handleSearch}
            showCancelButton
            closeButtonLabel="Confirm"
            onCancelButtonClick={() => setDisplayErrorMessage("")}
          />
        )}
        {showErrorMessage && (
          <InfoPopup
            headerText="Error"
            displayText={showErrorMessage}
            freeText
            error
            isOpen={!!showErrorMessage}
            onClose={() => setShowErrorMessage("")}
          />
        )}
        {validationError && (
          <InfoPopup
            headerText="Validation Error"
            displayText={validationError}
            freeText
            isOpen={!!validationError}
            onClose={() => setValidationError("")}
            closeButtonLabel="Got it"
          />
        )}
        {duplicatePending && (
          <InfoPopup
            headerText="Warning"
            displayText={`Duplicate condition detected:\n  Attribute: ${duplicatePending ? getAttributeDisplayName(duplicatePending.attribute) : ""}\n  Operator: ${arithmeticOperators.find((op) => op.value === duplicatePending?.operator)?.label ?? duplicatePending?.operator}\n  Value: ${duplicatePending?.value}\n\nDo you still want to add it?`}
            freeText
            warning
            isOpen={!!duplicatePending}
            onClose={() => {
              commitCondition(duplicatePending);
            }}
            showCancelButton
            closeButtonLabel="Proceed"
            onCancelButtonClick={() => setDuplicatePending(null)}
          />
        )}
        {loading && !skin && loadingSpinner}
        <div className={styles.scrollableContent}>
        <div className={`${styles.sectionDivider} nq-d1`}>
          <span className={styles.sectionLabel}>Objective</span>
        </div>
        <div className={styles.objectiveBlock}>
          <div className={styles.objectiveRow}>
            <label className={styles.label} style={{ margin: 0 }}>
              Objective <span className={styles.required}>*</span>
            </label>
            {allowedQueryObjectives?.length === 1 ? (
              <span className={styles.objectiveBadge}>
                {capitalize(allowedQueryObjectives[0])}
              </span>
            ) : (
              <>
                <div className={styles.objectivePillsRow}>
                  {objectiveOptions.map((opt) => (
                    <button
                      key={opt.value}
                      type="button"
                      className={`${styles.objectivePill}${formFields.objective === opt.value ? ` ${styles.objectivePillActive}` : ""}`}
                      onClick={() =>
                        setFormFields({ ...formFields, objective: opt.value })
                      }
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
                {!formFields.objective && (
                  <span className={styles.objectiveHint}>
                    — select one to continue
                  </span>
                )}
              </>
            )}
          </div>
          <LimitsUsageBanner
            loading={limitsUsageLoading}
            usage={objectiveUsage}
            selectedTables={selectedTables}
            inline
          />
          {isEnrichmentObjective && (
            <div className={styles.enrichmentWrapper}>
              <div className={styles.labelRow}>
                <label className={styles.label}>
                  Enrichment Attributes
                  {selectedAttributes.length > 0 && (
                    <span className={styles.selectionCount}>
                      {" "}
                      ({selectedAttributes.length} selected)
                    </span>
                  )}
                </label>
                <Tooltip text="Refresh the list of available enrichment attributes." placement="top">
                  <button
                    type="button"
                    className={styles.refreshIcon}
                    onClick={handleRefreshAttributes}
                  >
                    <ReloadIcon />
                    <span className={styles.refreshLabel}>Refresh</span>
                  </button>
                </Tooltip>
              </div>

              <div className={styles.enrichmentContainer}>
                {isRefreshing ? (
                  loadingSpinner
                ) : filteredEnrichmentAttributes?.length === 0 ? (
                  <span className={styles.textSmall}>
                    No enrichment attributes available for the selected dataset.
                  </span>
                ) : (
                  <>
                    {filteredEnrichmentAttributes.map((attr: string) => {
                      const isChecked = selectedAttributes.includes(attr);
                      return (
                        <button
                          key={attr}
                          type="button"
                          title={attr}
                          aria-pressed={isChecked}
                          className={`${styles.operatorBtn} ${styles.enrichmentChip}${isChecked ? ` ${styles.operatorBtnActive}` : ""}`}
                          onClick={() => toggleAttribute(attr)}
                        >
                          {isChecked && <span className={styles.checkIcon}>{(FEATURES.curationSkin ? <OI n="check" size="1em" /> : "✓")}</span>}
                          <span className={styles.enrichmentChipLabel}>{attr}</span>
                        </button>
                      );
                    })}
                    <div className={styles.enrichmentScrollFade} aria-hidden="true" />
                  </>
                )}
              </div>

              {isEnrichmentAttributesRequired && (
                  <span className={styles.errorMessage}>
                    Please select at least one attribute
                  </span>
                )}
            </div>
          )}
        </div>
        <div className={`${styles.sectionDivider} nq-d2`}>
          <span className={styles.sectionLabel}>Cohort Filters</span>
        </div>
        <div className={styles.formRow}>
          <div className={`${styles.formField} nq-step nq-s1`} data-state={stepState(attrPicked, true)}>
            <label className={styles.label}>
              <span className={styles.stepNumber}>1</span>
              Attributes <span className={styles.required}>*</span>
            </label>
            <Typeahead
              value={formFields.attribute}
              options={attributeOptions || []}
              onSelect={(attributeValue) => {
                setFormFields({ ...formFields, attribute: attributeValue, operator: "", value: "" });
              }}
              placeholder="Select attribute"
              style={{ width: "260px", flexShrink: 0 }}
              inputStyle={{
                height: "38px",
                boxSizing: "border-box",
                padding: "0 12px",
                fontSize: "var(--font-md)",
                fontFamily: '"Roboto", sans-serif',
                border: "1px solid #b7bec6",
                borderRadius: "4px",
                width: "100%",
              }}
            />
            {distinctDatasetCount > 1 && sortedAttributes?.[formFields.attribute]?.dataset_name && (
              <span className={styles.attributeBreadcrumb}>
                Filtering on <strong>{getAttributeDisplayName(formFields.attribute)}</strong> from{" "}
                {sortedAttributes[formFields.attribute].dataset_name}
              </span>
            )}
          </div>

          {formFields.attribute && !skin && <span className={styles.formDivider} aria-hidden="true">{(FEATURES.curationSkin ? <OI n="chevron" size="1em" /> : "›")}</span>}

          {(formFields.attribute || skin) && (
          <div className={`${styles.formField} nq-step nq-s2`} data-state={stepState(!!formFields.operator, attrPicked)}>
            <label className={styles.label}>
              <span className={styles.stepNumber}>2</span>
              Operator <span className={styles.required}>*</span>
            </label>
            {skin && !attrPicked ? emptySlot : skin && opsForAttribute.length > 3 ? (
              <select
                className={styles.select}
                value={formFields.operator}
                aria-label="Operator"
                onChange={(e) => setFormFields({ ...formFields, operator: e.target.value })}
              >
                <option value="">Select</option>
                {opsForAttribute.map((op) => (
                  <option key={op.value} value={op.value}>{op.label}</option>
                ))}
              </select>
            ) : (
            <div className={styles.operatorGroup}>
              {opsForAttribute.map((op) => {
                const isActive = formFields.operator === op.value;
                return (
                  <button
                    key={op.value}
                    type="button"
                    aria-pressed={isActive}
                    className={`${styles.operatorBtn}${isActive ? ` ${styles.operatorBtnActive}` : ""}`}
                    onClick={() =>
                      setFormFields({ ...formFields, operator: op.value })
                    }
                  >
                    {isActive && <span className={styles.checkIcon}>{(FEATURES.curationSkin ? <OI n="check" size="1em" /> : "✓")}</span>}
                    {op.label}
                  </button>
                );
              })}
            </div>
            )}
          </div>
          )}

          {formFields.attribute && !skin && <span className={styles.formDivider} aria-hidden="true">{(FEATURES.curationSkin ? <OI n="chevron" size="1em" /> : "›")}</span>}

          {(formFields.attribute || skin) && (
          <div className={`${styles.formField} nq-step nq-s3`} data-state={stepState(!!formFields.value, attrPicked && !!formFields.operator)}>
            <label className={styles.label}>
              <span className={styles.stepNumber}>3</span>
              Values <span className={styles.required}>*</span>
            </label>
            {skin && !attrPicked ? emptySlot : sortedAttributes?.[formFields.attribute]?.type === "int" ? (
              <input
                type="number"
                value={formFields.value}
                onChange={(e) =>
                  setFormFields({ ...formFields, value: e.target.value })
                }
                placeholder="Enter number"
                className={styles.textInput}
              />
            ) : (sortedAttributes?.[formFields.attribute]?.values?.length ?? 0) <= 4 ? (
              <div className={styles.operatorGroup}>
                {sortedAttributes?.[formFields.attribute]?.values?.map(
                  (val: any) => {
                    const isActive = formFields.value === String(val);
                    return (
                      <button
                        key={String(val)}
                        type="button"
                        aria-pressed={isActive}
                        className={`${styles.operatorBtn}${isActive ? ` ${styles.operatorBtnActive}` : ""}`}
                        onClick={() =>
                          setFormFields({ ...formFields, value: String(val) })
                        }
                      >
                        {isActive && <span className={styles.checkIcon}>{(FEATURES.curationSkin ? <OI n="check" size="1em" /> : "✓")}</span>}
                        {String(val)}
                      </button>
                    );
                  },
                )}
              </div>
            ) : (
              <select
                className={styles.select}
                value={formFields.value}
                onChange={(e) =>
                  setFormFields({ ...formFields, value: e.target.value })
                }
              >
                <option value="">Select</option>
                {sortedAttributes?.[formFields.attribute]?.values?.map(
                  (val: any) => (
                    <option key={String(val)} value={String(val)}>
                      {String(val)}
                    </option>
                  ),
                )}
              </select>
            )}
          </div>
          )}

          {formFields.attribute && !skin && <span className={styles.formDivider} aria-hidden="true">{(FEATURES.curationSkin ? <OI n="chevron" size="1em" /> : "›")}</span>}

          {(formFields.attribute || skin) && (
          <div className={`${styles.formField} nq-step nq-s4`} data-state={stepState(!!formFields.logicalOperator, false)}>
            <label className={styles.label}>
              <span className={styles.stepNumber}>4</span>
              Logical operators
            </label>
            {skin && !attrPicked ? emptySlot : (
            <div className={styles.operatorGroup}>
              {logicalOperators.map((logOp) => {
                const isActive = formFields.logicalOperator === logOp;
                return (
                  <button
                    key={logOp}
                    type="button"
                    aria-pressed={isActive}
                    className={`${styles.operatorBtn}${isActive ? ` ${styles.operatorBtnActive}` : ""}`}
                    onClick={() =>
                      setFormFields({
                        ...formFields,
                        logicalOperator: isActive ? "" : logOp,
                      })
                    }
                  >
                    {isActive && <span className={styles.checkIcon}>{(FEATURES.curationSkin ? <OI n="check" size="1em" /> : "✓")}</span>}
                    {logOp}
                  </button>
                );
              })}
            </div>
            )}
          </div>
          )}

          {formFields.attribute && !skin && <span className={styles.formDivider} aria-hidden="true">{(FEATURES.curationSkin ? <OI n="chevron" size="1em" /> : "›")}</span>}

          {(formFields.attribute || skin) && (
          <div className={`${styles.addButtonContainer} nq-add`}>
            <div className={styles.addButtonRow}>
              <button
                className={styles.addButton}
                onClick={handleAddCondition}
                disabled={!formFields.attribute || !formFields.operator || !formFields.value}
              >
                {skin ? (
                  <><OI n={editingIndex !== null ? "check" : "add"} size={14} />{editingIndex !== null ? "Update condition" : "Add condition"}</>
                ) : editingIndex !== null ? "Update" : "+ Add"}
              </button>
              {editingIndex !== null && (
                <button
                  type="button"
                  className={styles.cancelEditButton}
                  onClick={handleCancelEdit}
                >
                  Cancel
                </button>
              )}
            </div>
            {skin ? (
              <span className={styles.addHint} aria-live="polite">{nextHint}</span>
            ) : (!formFields.operator || !formFields.value) && (
              <span className={styles.addHint}>
                Fill in all required fields to add
              </span>
            )}
          </div>
          )}
        </div>
        <div className={`${styles.sectionDivider} nq-d3`}>
          <span className={styles.sectionLabel}>Cohort Brief</span>
        </div>
        <div className={styles.formRowSecond}>
          <div className={styles.nameFieldContainer} ref={nameFieldRef}>
            <label className={styles.label}>
              Cohort Name <span className={styles.required}>*</span>
            </label>
            <div className={styles.nameField}>
              <img
                src={SearchIcon}
                alt="Search"
                className={styles.searchIcon}
                onClick={() => setIsDropdownOpen(!isDropdownOpen)}
              />

              {/* Input Field */}
              <input
                type="text"
                placeholder={skin ? "New or existing cohort" : "Enter Name"}
                value={inputValue?.name}
                onChange={handleInputChange}
                maxLength={40}
              />

              {inputValue?.name && showPlusIcon && (
                  <Tooltip text="Create a new cohort" placement="top">
                    <img
                      src={PlusIcon}
                      alt="Add"
                      className={styles.plusIcon}
                      onClick={() => {
                        handleSelect({ id: "", name: inputValue.name });
                        setIsCampaignAdded(true);
                      }}
                    />
                  </Tooltip>
                )}

              {isCampaignAdded && (
                <img src={DoneIcon} alt="Done" className={styles.doneIcon} />
              )}
            </div>

            <div className={styles.fieldMeta}>
              {campaignNameError ? (
                <span className={styles.errorMessage}>{campaignNameError}</span>
              ) : (
                <span />
              )}
              <span
                className={`${styles.charCount}${(inputValue?.name?.length ?? 0) >= 36 ? ` ${styles.charCountWarn}` : ""}`}
              >
                {inputValue?.name?.length ?? 0} / 40
              </span>
            </div>

            {/* Autocomplete Dropdown — rendered via portal to escape overflow clipping */}
            {filteredCampaigns.length > 0 && dropdownRect &&
              ReactDOM.createPortal(
                <div
                  ref={dropdownPortalRef}
                  className={styles.dropdown}
                  style={{
                    position: "fixed",
                    top: dropdownRect.top,
                    left: dropdownRect.left,
                    width: dropdownRect.width,
                  }}
                >
                  {filteredCampaigns.map((campaign) => (
                    <div
                      key={campaign.id}
                      className={styles.dropdownItem}
                      onClick={() => handleSelect(campaign)}
                    >
                      {campaign.name}
                    </div>
                  ))}
                </div>,
                document.body,
              )
            }
          </div>
          <div className={styles.cpmFieldContainer}>
            <label className={styles.label} htmlFor="new-query-cpm">
              CPM (INR) <span className={styles.required}>*</span>
            </label>
            <div
              className={`${styles.nameField} ${styles.cpmField}${cpmError ? ` ${styles.fieldInvalid}` : ""}`}
            >
              <input
                id="new-query-cpm"
                type="text"
                inputMode="decimal"
                autoComplete="off"
                placeholder="e.g. 2.50"
                value={cpm}
                onChange={handleCpmChange}
                onBlur={() => setCpmError(validateCpm(cpm).error)}
                maxLength={12}
                required
                aria-required="true"
                aria-invalid={!!cpmError}
                aria-describedby={cpmError ? "new-query-cpm-error" : undefined}
              />
            </div>
            <div className={styles.fieldMeta}>
              {cpmError ? (
                <span
                  id="new-query-cpm-error"
                  role="alert"
                  className={styles.errorMessage}
                >
                  {cpmError}
                </span>
              ) : (
                <span />
              )}
            </div>
          </div>
          <div className={styles.descriptionFieldContainer}>
            <label className={styles.label}>Description</label>
            <div className={styles.descriptionField}>
              <textarea
                rows={1}
                placeholder="What is the goal of this cohort?"
                value={description}
                onChange={handleDescriptionChange}
              />
            </div>
            <div className={styles.fieldMeta}>
              {descriptionError ? (
                <span className={styles.errorMessage}>{descriptionError}</span>
              ) : (
                <span />
              )}
              <span
                className={`${styles.charCount}${description.length >= 90 ? ` ${styles.charCountWarn}` : ""}`}
              >
                {description.length} / 100
              </span>
            </div>
          </div>
        </div>
        {conditions.length === 0 ? (
          <div className={`${styles.emptyConditions} nq-conds`}>
            No filter conditions added yet — use the form above to build your
            query.
          </div>
        ) : (
          <>
            {conditions.some((c) => !c.logicalOperator) && (
              <div className={`${styles.defaultAndHint} nq-conds-hint`}>
                <InforIcon />
                Conditions without a logical operator are combined with AND by default.
              </div>
            )}
          <div className={`${styles.tableWrapper} nq-conds`}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>Attributes</th>
                <th>Operator</th>
                <th>Values</th>
                <th>Logical Operator</th>
                {skin ? <th className="nq-acts" aria-label="Actions" /> : <><th>Edit</th>
                <th>Remove</th></>}
              </tr>
            </thead>
            <tbody>
              {conditions.map((condition, index) => (
                <tr key={index} className={index === editingIndex ? styles.editingRow : undefined}>
                  <td>
                    <Tooltip text={getAttributeDisplayName(condition.attribute)} placement="top">
                      <span>{getAttributeDisplayName(condition.attribute)}</span>
                    </Tooltip>
                  </td>
                  <td>{arithmeticOperators.find((op) => op.value === condition.operator)?.label ?? condition.operator}</td>
                  <td>{condition.value}</td>
                  <td>{condition.logicalOperator || "—"}</td>
                  {skin ? (
                  <td className="nq-acts">
                    <button type="button" className="nq-act" title="Edit condition" aria-label="Edit condition" onClick={() => handleEditCondition(index)}><OI n="pen" /></button>
                    <button type="button" className="nq-act" title="Remove condition" aria-label="Remove condition" onClick={() => handleDeleteCondition(index)}><OI n="close" /></button>
                  </td>
                  ) : (<>
                  <td>
                    <img
                      src={EditIcon}
                      alt="Edit condition"
                      onClick={() => handleEditCondition(index)}
                      style={{ width: "18px", height: "18px" }}
                    />
                  </td>
                  <td>
                    <img
                      src={DeleteIcon}
                      alt="Remove condition"
                      onClick={() => handleDeleteCondition(index)}
                      style={{ width: "18px", height: "18px" }}
                    />
                  </td>
                  </>)}
                </tr>
              ))}
            </tbody>
          </table>
          </div>
          </>
        )}
        </div>
        <div className={styles.buttonContainer}>
          {skin ? (
            <span className={`nq-need${tableLimitBlockedMessage ? " is-blocked" : ""}`} aria-live="polite">
              {tableLimitBlockedMessage || (loading ? "Counting the audience across the federation." : stillNeeded.length ? `Still needed: ${listed(stillNeeded)}.` : "Ready to search.")}
            </span>
          ) : null}
          <div className={styles.tooltipContainer}>
            <button
              className={styles.searchButton}
              onClick={handleSearch}
              aria-busy={(skin && loading) || undefined}
              disabled={
                (skin && loading) ||
                !inputValue?.name ||
                validateCpm(cpm).value === null ||
                !!campaignNameError ||
                !!descriptionError ||
                (Object.keys(sortedAttributes || {}).length > 0 &&
                  conditions?.length === 0) ||
                !formFields.objective ||
                isEnrichmentSearchDisabled ||
                !!tableLimitBlockedMessage
              }
            >
              {skin && loading ? <><span className="onam-app nq-busy" aria-hidden="true"><Loader16 /></span>Searching</> : "Search"}
            </button>
            {skin ? null : tableLimitBlockedMessage ? (
              <span className={styles.tooltip}>{tableLimitBlockedMessage}</span>
            ) : (
              (!inputValue?.name ||
                validateCpm(cpm).value === null ||
                (Object.keys(sortedAttributes || {}).length > 0 &&
                  conditions?.length === 0) ||
                !formFields.objective ||
                isEnrichmentSearchDisabled) && (
                <span className={styles.tooltip}>
                  Please fill in the mandatory fields
                </span>
              )
            )}
          </div>
        </div>
      </div>
    </LayoutContainer>
  );
};

export default NewQuery;
